import * as Graph from "../../GeneratedGraphQL";

import { getContrastWithWhite } from "./utils/getContrastWithWhite";

// Names of the custom font families.
export type CustomFonts = { bold: string; regular: string };

/* eslint-disable @typescript-eslint/member-ordering */
export type Theme = {
  colours: {
    /**
     * Primary custom colour set by the client.
     */
    primary: string;
    /**
     * Colour of text to appear on a background that is the primary colour.
     * Can be either white or dark, based on contrast ratio.
     */
    textOnPrimary: string;
    /**
     * Colour of text intended to be of the primary colour on a white
     * background. Usually the same as primary, but may be changed to dark if
     * the contrast ratio of the primary colour and white is too low.
     */
    primaryText: string;

    /**
     * Secondary brand colour set by the client
     */
    secondary: string;
    /**
     * Colour of text to appear on a background that is the secondary colour.
     * Can be either white or dark, based on contrast ratio.
     */
    textOnSecondary: string;
    /**
     * Colour of text intended to be of the secondary colour on a white
     * background. Usually the same as secondary, but may be changed to dark if
     * the contrast ratio of the secondary colour and white is too low.
     */
    secondaryText: string;

    white: string;
    dark: string;
    black: string;

    error: string;
    success: string;
    info: string;

    /**
     * Boolean values representing contrast ratios of primary and secondary
     * custom colours as foregrounds and backgrounds.
     */
    isDark: {
      /**
       * True if the primary custom colour is considered a dark background (WCAG
       * contrast ratio >= 3)
       */
      primaryBackground: boolean;
      /**
       * True if the primary custom colour is considered a dark foreground (WCAG
       * contrast ratio >= 4.5)
       */
      primaryForeground: boolean;
      /**
       * True if the secondary custom colour is considered a dark background
       * (WCAG contrast ratio >= 3)
       */
      secondaryBackground: boolean;
      /**
       * True if the secondary custom colour is considered a dark foreground
       * (WCAG contrast ratio >= 4.5)
       */
      secondaryForeground: boolean;
    };
  };
  fonts: CustomFonts;
};
/* eslint-enable @typescript-eslint/member-ordering */

type GetThemeOptions = {
  customColours?: Graph.Maybe<Graph.CustomColours>;
  customFonts?: CustomFonts;
};

/**
 * Contrast threshold for coloured foreground on a white background. If the
 * contrast ratio is lower than this number, we display dark foreground instead.
 */
const foregroundContrastThreshold = 4.5;
/**
 * Contrast threshold for white foreground on a coloured background. If the
 * contrast ratio is lower than this number, we display dark foreground instead.
 */
const backgroundContrastThreshold = 3;

/**
 * Non-customisable colours
 */
const white = "#FFFFFF";
const black = "#111";
const dark = "#1A1A1A";
const error = "#D0051C";
const success = "#218C60";
const info = "#175A94";
export const nonCustomisableColours = {
  black,
  dark,
  error,
  info,
  success,
  white,
};

/**
 * Default custom colours
 */
const defaultPrimary = "#583976";
const defaultSecondary = "#3C5B58";
export const defaultCustomColours = {
  primary: defaultPrimary,
  secondary: defaultSecondary,
};

/**
 * Default fonts
 */
export const defaultRegularFont = "Lato";
export const defaultBoldFont = "Lato";
const defaultCustomFonts = {
  bold: defaultBoldFont,
  regular: defaultRegularFont,
};

/**
 * Generates a theme object containing style information for use throughout the
 * BookingTools.
 */
export function getTheme(
  options: GetThemeOptions = {
    customColours: defaultCustomColours,
    customFonts: defaultCustomFonts,
  },
): Theme {
  const customColours = options.customColours ?? defaultCustomColours;
  const customFonts = options.customFonts ?? defaultCustomFonts;
  const { primary: customPrimary, secondary: customSecondary } = customColours;

  const primary = customPrimary ?? defaultPrimary;
  const secondary = customSecondary ?? defaultSecondary;

  const isDark = {
    primaryBackground:
      getContrastWithWhite(primary) >= backgroundContrastThreshold,
    primaryForeground:
      getContrastWithWhite(primary) >= foregroundContrastThreshold,
    secondaryBackground:
      getContrastWithWhite(secondary) >= backgroundContrastThreshold,
    secondaryForeground:
      getContrastWithWhite(secondary) >= foregroundContrastThreshold,
  };

  const textOnPrimary = isDark.primaryBackground ? white : dark;
  const primaryText = isDark.primaryForeground ? primary : dark;

  const textOnSecondary = isDark.secondaryBackground ? white : dark;
  const secondaryText = isDark.secondaryForeground ? secondary : dark;

  /* eslint-disable sort-keys */
  const colours = {
    primary,
    textOnPrimary,
    primaryText,

    secondary,
    textOnSecondary,
    secondaryText,

    isDark,

    ...nonCustomisableColours,
  };
  /* eslint-enable sort-keys */

  return {
    colours,
    fonts: customFonts,
  };
}

/**
 * The default theme to be used if no custom colours or fonts are provided, or
 * in components which render before the Booking Tool config is loaded.
 */
export const defaultTheme: Theme = getTheme();

/**
 * A default theme with light custom colours, mostly for testing and storybook
 */
export const defaultLightTheme: Theme = getTheme({
  customColours: { primary: "#78ECE5", secondary: "#E7D29C" },
});
